/**
 * @file ProductsSourceField.tsx
 * @description «Какие товары» элемента «Товары» (phase19.16 Р2;
 * uiux-design-r2 §4 ASCII, §10; SB32 — «Новинка» только сама, по дате):
 * (•) Новинки · ( ) С бейджем [Бестселлер ▾] · ( ) Выбрать вручную (список
 * с ⋮⋮ и ✕). Счётчик «Сейчас подходит» — `DataCountLine` в панели.
 * Подпись «Какие товары» — заголовок подгруппы панели (SB122), здесь её нет.
 */

import { SECTION_PRODUCT_BADGES, SITE_LIMITS, type SectionElementOf, type SectionProductBadge } from "@chaiberi/shared";

import { SelectField } from "../../../ui/SelectField";
import { Segmented } from "../../../site-view/Segmented";
import { IdPickField } from "../fields/IdPickField";
import { PRODUCT_BADGE_LABELS } from "../../../../lib/site-builder/section-meta";

type Products = SectionElementOf<"products">;

const SOURCES = [
  { value: "new" as const, label: "Новинки" },
  { value: "badge" as const, label: "С бейджем" },
  { value: "manual" as const, label: "Вручную" },
];

const SOURCE_HINT: Readonly<Record<Products["source"], string>> = {
  new: "Сами, по дате появления в каталоге.",
  badge: "Товары с выбранным бейджем из карточки товара.",
  manual: "Только выбранные товары в этом порядке.",
};

interface Props {
  element: Products;
  onChange: (element: Products) => void;
  idsError?: string | null;
  badgeError?: string | null;
}


export function ProductsSourceField({ element, onChange, idsError, badgeError }: Props) {
  return (
    <div className="flex flex-col gap-2">
      <Segmented
        aria-label="Какие товары"
        options={SOURCES}
        value={element.source}
        onChange={(source) => onChange({ ...element, source, badge: source === "badge" ? (element.badge ?? "BESTSELLER") : element.badge })}
      />
      <p className="type-caption text-on-surface-variant">{SOURCE_HINT[element.source]}</p>
      {element.source === "badge" ? (
        <>
          <SelectField
            ariaLabel="Бейдж"
            value={element.badge ?? ""}
            placeholder="Выберите бейдж"
            options={SECTION_PRODUCT_BADGES.map((b) => ({ value: b, label: PRODUCT_BADGE_LABELS[b] }))}
            onChange={(v) => onChange({ ...element, badge: v as SectionProductBadge })}
          />
          {badgeError ? <p className="type-caption text-destructive">{badgeError}</p> : null}
        </>
      ) : null}
      {element.source === "manual" ? (
        <IdPickField
          label="Товары"
          list="products"
          ids={element.ids}
          max={SITE_LIMITS.manualProducts}
          addPlaceholder="+ Добавить товар"
          error={idsError}
          onChange={(ids) => onChange({ ...element, ids })}
        />
      ) : null}
    </div>
  );
}
